import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, MenuItem, Stack, TextField } from '@mui/material';
import { ReserveStock } from '../../api/dto';
import { reserveStockSchema } from '../../utils/zodSchemas';
import { useReleaseStock } from '../../api/queries';
import { fr } from '../../i18n/fr';
import { STOCK_STATE_LABEL } from '../../utils/mapping';
import ProductSelectorField from './fields/ProductSelectorField';
import { ProductSelectionItem } from '../../types';

interface Props {
  storeId: string;
  initialProductId?: string;
  initialProduct?: ProductSelectionItem | null;
  onSubmitted?: () => void;
}

const ReleaseForm: React.FC<Props> = ({
  storeId,
  initialProductId,
  initialProduct,
  onSubmitted,
}) => {
  const mutation = useReleaseStock();
  const defaultValues = useMemo(
    () => ({
      store_id: storeId,
      stock_state: 'reserved' as const,
      quantity: 1,
      product_id: initialProductId ?? '',
    }),
    [storeId, initialProductId]
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
  } = useForm<ReserveStock>({
    resolver: zodResolver(reserveStockSchema),
    defaultValues,
  });

  useEffect(() => {
    setValue('store_id', storeId);
  }, [setValue, storeId]);

  useEffect(() => {
    setValue('product_id', initialProductId ?? '');
  }, [initialProductId, setValue]);

  const onSubmit = (values: ReserveStock) => {
    const payload: ReserveStock = {
      ...values,
      product_id: values.product_id.trim(),
    };

    mutation.mutate(payload, {
      onSuccess: () => {
        reset({
          ...defaultValues,
          stock_state: values.stock_state,
          quantity: values.quantity,
        });
        onSubmitted?.();
      },
    });
  };

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      spacing={2}
      aria-label="release-form"
    >
      <input type="hidden" {...register('store_id')} />
      {mutation.isError && (
        <Alert severity="error">{(mutation.error as Error).message}</Alert>
      )}
      <ProductSelectorField
        control={control}
        name="product_id"
        label={fr.product}
        error={!!errors.product_id}
        helperText={errors.product_id?.message}
        initialSelection={initialProduct ?? null}
      />
      <TextField select label={fr.stockState} {...register('stock_state')} fullWidth>
        {Object.entries(STOCK_STATE_LABEL).map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        type="number"
        label={fr.quantity}
        inputProps={{ min: 0, step: 1 }}
        {...register('quantity', { valueAsNumber: true })}
        error={!!errors.quantity}
        helperText={errors.quantity?.message}
        fullWidth
      />
      <Button type="submit" variant="contained" disabled={mutation.isPending}>
        {fr.saveMovement}
      </Button>
    </Stack>
  );
};

export default ReleaseForm;
